import React from 'react';
import { formatCurrency, formatCurrencyNumber, getCurrencySymbol } from '../utils/currency';
import '../styles/currency-value.css';

interface Props {
  value: number;
  currency?: string;
  large?: boolean;
  /** When true and |value| >= 1000, render compact form (k/M) instead of full with cents. */
  short?: boolean;
}

/**
 * Currency rendered with a styled symbol + muted decimal suffix.
 *
 *  short=false (default) → "€ 1 234.56"     (cents always shown)
 *  short=true,  |v| ≥ 1M → "€ 4.4M"          (decimal+M muted)
 *  short=true,  |v| ≥ 1k → "€ 10.5k"         (decimal+k muted)
 *  short=true,  |v| < 1k → "€ 123.45"        (falls back to full, cents muted)
 *
 * Styling relies on three classes shared across the app:
 *  - .currency-symbol  (small/muted; also .large variant)
 *  - .currency-whole   (full size / normal color)
 *  - .currency-cents   (small/muted; also .large variant)
 * defined in dashboard.css.
 */
const CurrencyValue: React.FC<Props> = ({ value, currency = 'EUR', large, short }) => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const symbol = getCurrencySymbol(currency);
  const symbolClass = `currency-symbol ${large ? 'large' : ''}`;
  const centsClass = `currency-cents ${large ? 'large' : ''}`;

  // Native browser tooltip with the exact amount — useful when short mode
  // hides decimals or KZT-style large numbers get truncated.
  const fullTitle = formatCurrency(value, currency);

  if (short && abs >= 1_000) {
    const divisor = abs >= 1_000_000 ? 1_000_000 : 1_000;
    const suffix = abs >= 1_000_000 ? 'M' : 'k';
    // "10.5k" — only the currency symbol is muted; the number and k/M
    // suffix render in the normal text color.
    return (
      <span className="currency-value has-tooltip" data-tooltip={fullTitle}>
        <span className={symbolClass}>{sign}{symbol}</span>
        <span className="currency-whole">{(abs / divisor).toFixed(1)}{suffix}</span>
      </span>
    );
  }

  // Full format: 1 234.56 with the cents+symbol muted.
  const [whole, cents] = formatCurrencyNumber(abs).split('.');
  return (
    <span className="currency-value" title={fullTitle}>
      <span className={symbolClass}>{sign}{symbol}</span>
      <span className="currency-whole">{whole}</span>
      <span className={centsClass}>.{cents}</span>
    </span>
  );
};

export default CurrencyValue;
